import ApiController from './ApiController';
import DatabaseController from './DatabaseController';
import Permissao from '../models/Permissao';

/**
 * Retorno dos métodos da classe
 * @typedef {Object} Retorno
 * @property {Boolean} sucesso Indica se a requisição foi completada e o retorno foi o esperado
 * @property {String} mensagem Indica a mensagem de erro
 */

/**
 * Retorno da validação do login, contendo as permissões do usuário
 * @typedef {Object} RetornoValidar
 * @property {Boolean} sucesso Indica se o usuário está logado ou não
 * @property {Array<Permissao>} permissoes Lista de permissões do usuário
 */

/**
  * Controller para realizar o login do usuário na API e gerenciar a autenticação na aplicação
  */
class LoginController {

    constructor() {
        this.databaseController = new DatabaseController();
        this.apiController = new ApiController();
    }

    /**
     * Realiza o login do usuário na API e captura o token JWT
     * @param {String} login Login do usuário na plataforma
     * @param {String} senha Senha do usuário na plataforma
     * @returns {Promise<Retorno>} Retorna um objeto indicando se houve sucesso ou erro no login
     */
    async login(login, senha) {
        if (!login)
            throw new TypeError('Login é necessário');
        if (!senha)
            throw new TypeError('Senha é necessário');

        try {
            let response = await this.apiController.post('/authenticate', { 'usuario': login, 'senha': senha }, false);
            if (response.data.token) {
                //Salva os dados
                const { usuario, empresa, token } = response.data;
                await this.salvarSecao(usuario, empresa, token);
                //Retorna indicando que houve sucesso
                return { sucesso: true };
            } else
                return { sucesso: false, mensagem: 'Não foi possível realizar a autenticação' };
        } catch (error) {
            const mensagem = error.response && error.response.data && error.response.data[0]
                ? error.response.data[0].mensagem
                : (error.message || 'Erro de conexão com o servidor');
            return { sucesso: false, mensagem };
        }
    }

    /**
     * Realiza o logout do usuário da aplicação
     * @returns {Promise} Retorna um promise para execução assíncrona 
     */
    async logout() {
        await this.salvarSecao(undefined, undefined, undefined);
    }

    /**
     * Verifica se o usuário está logado na plataforma
     * @returns {Promise<RetornoValidar>} Retorna verdadeiro se o usuário estiver logado, falso caso contrário
     */
    async estaLogado() {
        try {
            const token = await this.databaseController.carregar('config', 'token');
            if (token) {
                //Chama o método da API
                const response = await this.apiController.get('/validateAuthentication');
                const result = {
                    sucesso: true,
                    permissoes: response.data.usuario.permissoes.map(permissao => Permissao.clone(permissao))
                };
                //Salva os dados
                const { usuario, empresa, token } = response.data;
                await this.salvarSecao(usuario, empresa, token);
                //Caso execute com sucesso, retorna verdadeiro
                return result;
            } else {
                return { sucesso: false };
            }
        } catch (ex) {
            //Caso ocorra algum erro, retorna falso e limpa o token
            this.logout();
            return { sucesso: false };
        }
    }

    /**
     * Envia uma requisição para resetar a senha do usuário
     * @param {String} email Endereço de e-mail que será enviada a mensagem de recuperação
     * @return {Promise} Retorna a resposta do servidor, indicando se houve sucesso ou falha
     */
    async recuperarSenha(email) {
        if (!email)
            return { sucesso: false, mensagem: 'E-mail é necessário' };

        try {
            await this.apiController.post('/passwordRecovery', { 'email': email }, false);
            return { sucesso: true };
        } catch (error) {
            if (error.response && error.response.data)
                return { sucesso: false, mensagem: error.response.data[0].mensagem };
            else
                return { sucesso: false, mensagem: error.message || 'Ocorreu um erro desconhecido ao enviar o e-mail para recuperação de senha.' };
        }
    }

    /**
     * Envia uma requisição para alterar a senha do usuário
     * @param {String} token Token utilizado para garantir a autenticidade da requisição
     * @param {String} senha Nova senha do usuário
     * @param {String} confirmacao Confirmação de que o usuário digitou corretamente
     * @return {Promise} Retorna a resposta do servidor, indicando se houve sucesso ou falha
     */
    async redefinirSenha(token, senha, confirmacao) {
        if (!token)
            return { sucesso: false, mensagem: 'É necessário um token para alteração' };
        if (!senha)
            return { sucesso: false, mensagem: 'É necessário informar a senha' };
        if (senha !== confirmacao)
            return { sucesso: false, mensagem: 'As senhas não conferem' };

        try {
            await this.apiController.post('/passwordReset', { token, senha }, false);
            return { sucesso: true };
        } catch (error) {
            const mensagem = error.response && error.response.data && error.response.data[0]
                ? error.response.data[0].mensagem
                : (error.message || 'Erro de conexão com o servidor');
            return { sucesso: false, mensagem };
        }
    }

    /**
     * Salva os dados da seção atual
     * @param {Object} usuario Dados do usuário autenticado
     * @param {Object} empresa Dados da empresa relacionada ao usuário logado
     * @param {String} token Token de acesso para as próximas requisições
     * @returns {Promise} Retorna um promise a ser resolvido quando todos os valores forem inseridos
     */
    salvarSecao(usuario, empresa, token) {
        return Promise.all([
            this.databaseController.salvar('config', 'usuario', usuario),
            this.databaseController.salvar('config', 'empresa', empresa),
            this.databaseController.salvar('config', 'token', token)
        ]);
    }

    /**
     * Inscreve ao evento de sessão expirada
     * @param {Function} callback Método a ser invocado quando o evento é disparado
     */
    static watchSessionExpired(callback) {
        //Valida se o parâmetro foi informado e é uma função
        if (!callback)
            throw new TypeError('É necessário passar um callback');
        else if (typeof callback !== 'function')
            throw new TypeError('Callback precisa ser uma função');

        //Caso a lista não exista, cria um novo array
        if (!LoginController._sessionExpiredCallbackList)
            LoginController._sessionExpiredCallbackList = [];

        //Verificando se o método está na lista
        const indice = LoginController._sessionExpiredCallbackList.indexOf(callback);
        if (indice === -1)
            LoginController._sessionExpiredCallbackList.push(callback);
    }

    /**
     * Cancela inscrição do evento de sessão expirada
     * @param {Function} callback Método a ser retirado da lista de invocação
     */
    static unwatchSessionExpired(callback) {
        //Valida se o parâmetro foi informado e é uma função
        if (!callback)
            throw new TypeError('É necessário passar um callback');
        else if (typeof callback !== 'function')
            throw new TypeError('Callback precisa ser uma função');

        //Caso a lista não exista, cria um novo array
        if (!LoginController._sessionExpiredCallbackList)
            LoginController._sessionExpiredCallbackList = [];

        //Verificando se o método está na lista
        const indice = LoginController._sessionExpiredCallbackList.indexOf(callback);
        if (indice > -1)
            LoginController._sessionExpiredCallbackList.splice([].indexOf(callback), 1);
    }

    /**
     * Dispara todos os métodos que estão inscritos no evento de sessão expirada
     */
    static fireSessionExpired() {
        //Verifica se a lista existe
        if (LoginController._sessionExpiredCallbackList) {
            //Percorre a lista, chamado todos os callbacks
            LoginController._sessionExpiredCallbackList.forEach(callback => {
                try {
                    callback();
                } catch (ignore) {
                    //Se algum callback falhar, apenas ignora
                }
            });
        }
    }
}

export default LoginController;